import type { AxiosInstance } from 'axios'
import Cookies from 'js-cookie'
import { SENDHUB_SESSION_COOKIE_NAME } from './config'

/**
 * Forwarding the SendHub session as a header rather than relying on the cookie.
 *
 * When embedded in the webclient our bundle runs on the *embedding* origin
 * (e.g. dev.sendhub.com) while this app's API lives on another
 * (integrations-dev.sendhub.com). SendHub's session cookie is host-only in HTTP
 * dev environments — its config_base.py forces SESSION_COOKIE_DOMAIN=None there,
 * because Chrome drops a Domain-scoped cookie sent over HTTP to a private IP —
 * so the browser never sends it cross-origin and the API sees no session.
 *
 * SendHub's sanctioned workaround is to pass the session key explicitly in the
 * X-SH-Session-Key header, which both our API and SendHub itself accept. The
 * cookie is only readable from JS because SendHub leaves it non-HttpOnly
 * outside production; where it isn't readable we simply send nothing and fall
 * back to normal cookie auth.
 */
export const SENDHUB_SESSION_KEY_HEADER = 'X-SH-Session-Key'

export function getSendhubSessionKey(): string | undefined {
  // The embedding page knows best: its SH.getSessionKey() also honours a
  // per-tab impersonation override and any cookie name it uses. Fall back to
  // our own cookie read when it isn't there or has nothing.
  try {
    const sh = (window as unknown as { SH?: { getSessionKey?: () => unknown; BOOTSTRAP?: { session_key?: unknown } } }).SH
    const fromHost = sh?.getSessionKey?.()
    if (typeof fromHost === 'string' && fromHost && fromHost !== 'null') return fromHost
    // Older webclients only expose the key on the bootstrap object.
    const fromBootstrap = sh?.BOOTSTRAP?.session_key
    if (typeof fromBootstrap === 'string' && fromBootstrap) return fromBootstrap
  } catch {
    // A host bridge that throws must not take auth down with it.
  }
  return Cookies.get(SENDHUB_SESSION_COOKIE_NAME)
}

/** Header bag for `fetch` callers; empty when no session key is readable. */
export function sendhubSessionKeyHeader(): Record<string, string> {
  const sessionKey = getSendhubSessionKey()
  return sessionKey ? { [SENDHUB_SESSION_KEY_HEADER]: sessionKey } : {}
}

/** Attach the session key to every request an axios instance makes. */
export function attachSendhubSessionKey(instance: AxiosInstance): void {
  instance.interceptors.request.use((requestConfig) => {
    const sessionKey = getSendhubSessionKey()
    if (sessionKey) requestConfig.headers.set(SENDHUB_SESSION_KEY_HEADER, sessionKey)
    return requestConfig
  })
}
